import styled from 'styled-components';
import { readableColor, rgba } from 'polished';
import breakpoint from 'Styles/breakpoints';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import { fontTitle, fontBody } from 'Styles/fonts';
import Layout from '../Layout/Layout';
import OriginalCarousel from '../Carousel/Carousel';
import OriginalCTABlock from '../CTABlock/CTABlock';
import TextCard from '../TextCard/TextCard';
import { Title, Copy, Subheader, Cta } from '../TextCard/TextCard.styled';

/**
 * Util function to choose the correct spacing depending
 * on if the Layout Spacing is 'Roomy' or 'Compact'
 *
 * @param {Number} CompactValue - The value of the Compact prop
 * @param {Number} RoomyValue - The value of the Roomy prop
 * @returns {Number} - The value of the compact or roomy prop
 */
const getSpacingValue =
  (CompactValue = 8, RoomyValue = 8) =>
  ({ $layoutSpacing }) =>
    $layoutSpacing === 'Compact' ? CompactValue : RoomyValue;

export const CTABlock = styled(OriginalCTABlock)`
  margin: 8px 16px 0 16px;
  padding-top: 16px;
  border-top: 1px solid ${rgba(convertColorToThemeHexCode('black'), 0.1)};

  ${breakpoint('medium')} {
    border: none;
    padding-top: 0;
  }
`;

export const CardLayout = styled(Layout)`
  && {
    padding: 0;
    ${({ $totalColsInGrid = 12 }) =>
      $totalColsInGrid === 10 &&
      'grid-template-columns: repeat(10, minmax(0, 125.6px));'}

    ${({ $isContainerCentered, $totalColsFromCards, $withTop }) => {
      if ($isContainerCentered && $totalColsFromCards < 12 && $withTop) {
        return `grid-template-columns: repeat(${$totalColsFromCards}, minmax(0, 101px));`;
      }
    }}
  }
`;

export const CopyContainer = styled.div`
  &:empty {
    display: none;
  }

  grid-column: 1 / -1;

  ${({ $headerAlignment }) => `text-align: ${$headerAlignment};`}
  color: ${({ $backgroundColor }) =>
    readableColor(
      $backgroundColor,
      convertColorToThemeHexCode('white'),
      convertColorToThemeHexCode('black')
    )};

  ${({ $isBleeding }) =>
    $isBleeding &&
    `
      padding-left: 16px;
      padding-right: 16px;

      ${breakpoint('medium')} {
        padding-left: 24px;
        padding-right: 24px;
      }
    `}
`;

export const Header = styled.h2`
  ${fontTitle(24)}
  color: inherit;
  margin-bottom: 4px;

  ${breakpoint('medium')} {
    ${fontTitle(32)}
    margin-bottom: 8px;
  }

  ${breakpoint('large')} {
    ${fontTitle(40)}
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SubHeader = styled.div`
  ${fontBody(16)}
  color: inherit;

  ${breakpoint('large')} {
    ${fontBody(18)}
  }
`;

export const Carousel = styled(OriginalCarousel)`
  grid-column: 1 / -1;
  margin: unset;
`;

export const TitleCard = styled(TextCard)`
  position: relative;

  grid-column: span 12;
  height: 100%;
  padding-bottom: 12px;
  align-items: flex-start;

  ${Title} {
    ${fontTitle(24)}
    margin-bottom: unset;

    ${breakpoint('medium')} {
      ${fontTitle(32)}
    }
  }

  ${Copy} {
    padding: 12px;

    ${breakpoint('medium')} {
      width: 50%;
    }

    ${breakpoint('large')} {
      width: inherit;
      padding: 12px 24px 24px 12px;
    }
  }

  ${Subheader} {
    margin-top: 8px;
  }

  ${Subheader} * {
    ${fontBody(14)}
  }

  ${Cta} {
    margin-top: 16px;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    width: 100%;
    background: var(--foreground);
    opacity: 20%;
    z-index: 100;
  }

  ${breakpoint('large')} {
    ${({ gridSpan }) => gridSpan && `grid-column: span ${gridSpan};`}

    &:after {
      content: '';
      top: 0;
      bottom: 0;
      right: -1px;
      left: unset;
      height: 100%;
      width: 1px;
    }
  }
`;

export const CardContainer = styled(Layout)`
  && {
    grid-template-columns: none;
    grid-gap: ${getSpacingValue(8, 16)}px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};

    padding-top: ${({ hasTopPadding }) =>
      hasTopPadding ? getSpacingValue(16, 40) : 0}px;
    padding-bottom: ${getSpacingValue(16, 40)}px;

    ${({ $isBleeding }) => {
      if ($isBleeding) {
        return 'padding-left: 0; padding-right: 0;';
      }
    }}

    ${breakpoint('medium')} {
      grid-gap: ${getSpacingValue(12, 24)}px;
      padding-top: ${({ hasTopPadding }) =>
        hasTopPadding ? getSpacingValue(24, 56) : 0}px;
      padding-bottom: ${getSpacingValue(24, 56)}px;
    }

    ${breakpoint('large')} {
      grid-gap: ${getSpacingValue(16, 32)}px;
      padding-top: ${({ hasTopPadding }) =>
        hasTopPadding ? getSpacingValue(24, 64) : 0}px;
      padding-bottom: ${getSpacingValue(24, 64)}px;
    }

    /* selector here to support spacing changes when title is on top */
    ${CTABlock} {
      padding-top: ${getSpacingValue(16, 32)}px;

      ${breakpoint('medium')} {
        padding-top: ${getSpacingValue(4, 16)}px;
      }

      ${breakpoint('large')} {
        padding-top: ${getSpacingValue(6, 8)}px;
      }
    }
  }
`;

export const Center = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`;

export const CenteredCardWrapper = styled.div`
  &:not(:last-of-type) {
    padding-right: 16px;

    ${breakpoint('medium')} {
      padding-right: 24px;
    }
  }

  ${({ $span }) => {
    if ($span) {
      const width = ($span / 12) * 100;

      return `max-width: ${width}%; flex-basis: ${width}%`;
    }
  }}
`;

export default {};
